import { loadResearchSharing } from '@/composables/researchSharing'
// 引入 Vue Router 核心函数
import { createRouter } from 'vue-router'
import { reloadForStaleChunk } from '@/utils/staleChunkReload'
import { createEntryHistory, giftHistoryNavigationTarget, isShortGiftPath } from './giftHistory'

// 路由规则：path 是访问路径，component 是对应组件
const routes = [
  {
    path: '/',  // 默认入口（访问 localhost 对应主页）
    name: 'Home',
    component: () => import('../views/homePage.vue'),
    meta: { requiresAuth: false, title: '首页 - BioIntelOS' }
  },
  {
    path: '/dataset',
    name: 'Dataset',
    component: () => import('../views/dataset/DatasetPage.vue'),
    meta: { requiresAuth: true, title: '数据集浏览 - BioIntelOS' }
  },
  {
    path: '/dataset/:datasetNo',
    name: 'dataset-detail',
    component: () => import('../views/detailPage.vue'),
    meta: { requiresAuth: true, title: '数据集详情 - BioIntelOS' }
  },
  {
    path: '/community',
    name: 'ResearchCommunity',
    component: () => import('../views/community/ResearchCommunityPage.vue'),
    meta: { requiresAuth: true, researchSharing: true, title: '成果社区 - BioIntelOS' }
  },
  {path:'/community/:token',name:'ResearchDetail',component:()=>import('../views/community/ResearchDetailPage.vue'),meta:{requiresAuth:true,researchSharing:true,title:'成果详情 - BioIntelOS'}},
  {path:'/my-shares/import',name:'ImportSharedFiles',component:()=>import('../views/community/ImportSharedFiles.vue'),meta:{requiresAuth:true,researchSharing:true,title:'保存到我的云盘 - BioIntelOS'}},
  {path:'/my-shares',name:'MyResearchShares',component:()=>import('../views/community/MyResearchShares.vue'),meta:{requiresAuth:true,researchSharing:true,title:'我的分享 - BioIntelOS'}},
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/loginPage.vue'),
    meta: { requiresAuth: false, title: '登录 - BioIntelOS' }
  },
  {
    path: '/onboarding',
    name: 'Onboarding',
    component: () => import('../views/onboardingPage.vue'),
    meta: { requiresAuth: true, title: '完善研究画像 - BioIntelOS' }
  },
  {
    path: '/terms',
    name: 'Terms',
    component: () => import('../views/termsPage.vue'),
    meta: { requiresAuth: false, title: '服务条款 - BioIntelOS' }
  },
  {
    path: '/privacy',
    name: 'Privacy',
    component: () => import('../views/privacyPage.vue'),
    meta: { requiresAuth: false, title: '隐私政策 - BioIntelOS' }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('../views/profilePage.vue'),
    meta: { requiresAuth: true, title: '个人中心 - BioIntelOS' }
  },
  {
    path: '/ai-chat',
    name: 'AiChat',
    component: () => import('../views/aiChatPage.vue'),
    meta: { requiresAuth: true, title: 'AI 对话 - BioIntelOS' }
  },
  {
    path: '/billing',
    name: 'Billing',
    component: () => import('../views/CreditCenterRouter.vue'),
    meta: { requiresAuth: true, title: '计费中心 - BioIntelOS' }
  },
  {
    // 旧个人发票页已下线，统一走计费中心「订单记录」；兼容旧链接与消息通知深链
    path: '/invoices',
    redirect: (to) => ({ path: '/billing', query: { ...to.query, tab: 'orders' } })
  },
  {
    path: '/feedback',
    name: 'Feedback',
    component: () => import('../views/FeedbackPage.vue'),
    meta: { requiresAuth: true, title: '问题反馈 - BioIntelOS' }
  },
  {
    path: '/recharge',
    name: 'Recharge',
    component: () => import('../views/CreditCenterRouter.vue'),
    meta: { requiresAuth: true, title: '快速充值 - BioIntelOS' }
  },
  {
    path: '/g/:code([A-Za-z][A-Za-z0-9]{7})',
    name: 'SubscriptionGiftShortClaim',
    component: () => import('../views/SubscriptionGiftClaim.vue'),
    meta: { requiresAuth: false, title: '领取会员 - BioIntelOS' }
  },
  {
    path: '/subscription-gifts/:giftId',
    name: 'SubscriptionGiftClaim',
    component: () => import('../views/SubscriptionGiftClaim.vue'),
    meta: { requiresAuth: true, title: '领取会员 - BioIntelOS' }
  },
  {
    path: '/my-credits',
    name: 'MyCredits',
    component: () => import('../views/MyCredits.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/inspiration',
    name: 'Inspiration',
    component: () => import('../views/inspiration/InspirationPage.vue'),
    meta: { requiresAuth: true, title: '灵感 - BioIntelOS' }
  },
  {
    path: '/capabilities',
    name: 'CapabilitySpace',
    component: () => import('../views/capabilities/CapabilitySpacePage.vue'),
    meta: { requiresAuth: true, title: '能力空间 - BioIntelOS' }
  },
  {
    path: '/capabilities/scenario-research',
    name: 'ScenarioResearchProducts',
    component: () => import('../views/capabilities/ScenarioResearchProductsPage.vue'),
    meta: { requiresAuth: true, title: '场景化研究产品 - BioIntelOS' }
  },
  {
    // 产品二创建/运行均由 GutDB 页面承载；xBIOS 只在“个性化调整”时作为对话入口。
    path: '/inspiration/tcm-clinical/create',
    name: 'TcmClinicalCreate',
    component: () => import('../views/inspiration/tcm-clinical/TcmClinicalCreatePage.vue'),
    meta: { requiresAuth: true, title: '创建中医药临床数据分析 - BioIntelOS' }
  },
  {
    path: '/inspiration/tcm-clinical/analyses/:analysisId/runs/:runId',
    name: 'TcmClinicalRun',
    component: () => import('../views/inspiration/tcm-clinical/TcmClinicalRunPage.vue'),
    meta: { requiresAuth: true, title: '中医药临床数据分析 - BioIntelOS' }
  },
  {
    // 产品三使用独立工作流标识，创建、配置和执行都由 GutDB 页面承载。
    path: '/inspiration/tcm-microbiota/create',
    name: 'TcmMicrobiotaCreate',
    component: () => import('../views/inspiration/tcm-microbiota/TcmMicrobiotaCreatePage.vue'),
    meta: { requiresAuth: true, title: '创建多组学机制链整合分析 - BioIntelOS' }
  },
  {
    path: '/inspiration/tcm-microbiota/analyses/:analysisId/runs/:runId',
    name: 'TcmMicrobiotaDraft',
    component: () => import('../views/inspiration/tcm-microbiota/TcmMicrobiotaDraftPage.vue'),
    meta: { requiresAuth: true, title: '多组学机制链配置 - BioIntelOS' }
  },
  {
    path: '/inspiration/templates/:slug/create',
    name: 'AnalysisCreate',
    component: () => import('../views/inspiration/AnalysisCreatePage.vue'),
    meta: { requiresAuth: true, title: '创建标准分析 - BioIntelOS' }
  },
  {
    path: '/inspiration/analyses/:analysisId',
    name: 'AnalysisRun',
    component: () => import('../views/inspiration/AnalysisRunPage.vue'),
    meta: { requiresAuth: true, title: '标准分析 - BioIntelOS' }
  },
  {
    path: '/starmap',
    name: 'StarMap',
    redirect: { name: 'AiChat' }
  },
  {
    path: '/promotion',
    name: 'Promotion',
    component: () => import('../views/promotionPage.vue'),
    meta: { requiresAuth: true, requiresPersonalUser: true, title: '推广赚碱基币 - BioIntelOS' }
  },
  {
    path: '/promotion-modal',
    name: 'PromotionModal',
    component: () => import('../views/PromotionModal.vue'),
    meta: { requiresAuth: true, title: '邀请好友 - BioIntelOS' }
  },
  {
    path: '/enterprise',
    component: () => import('../views/enterprise/EnterpriseLayout.vue'),
    meta: { requiresAuth: true, requiresEnterpriseAdmin: true },
    redirect: '/enterprise/dashboard',
    children: [
      {
        path: 'dashboard',
        name: 'EnterpriseDashboard',
        component: () => import('../views/enterprise/EnterpriseDashboard.vue'),
      },
      {
        path: 'members',
        name: 'EnterpriseMembers',
        component: () => import('../views/enterprise/EnterpriseMembers.vue'),
      },
      {
        path: 'quota',
        name: 'EnterpriseQuota',
        component: () => import('../views/enterprise/EnterpriseQuota.vue'),
      },
      {
        path: 'usage',
        name: 'EnterpriseUsage',
        component: () => import('../views/enterprise/EnterpriseUsage.vue'),
      },
      {
        path: 'billing',
        name: 'EnterpriseBilling',
        component: () => import('../views/enterprise/EnterpriseBilling.vue'),
      },
      {
        path: 'invoices',
        name: 'EnterpriseInvoices',
        component: () => import('../views/enterprise/EnterpriseInvoices.vue'),
      },
    ],
  },
  {
    path: '/admin',
    component: () => import('../views/admin/AdminLayout.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: '管理后台 - BioIntelOS' },
    redirect: '/admin/dashboard',
    children: [
      {
        path: 'dashboard',
        name: 'AdminDashboard',
        component: () => import('../views/admin/AdminDashboard.vue'),
        meta: { title: '仪表盘 - 管理后台 - BioIntelOS' }
      },
      {
        path: 'users',
        name: 'AdminUsers',
        component: () => import('../views/admin/AdminUsers.vue'),
        meta: { title: '用户管理 - 管理后台 - BioIntelOS' }
      },
      {
        path: 'demo-datasets',
        name: 'AdminDemoDatasets',
        component: () => import('../views/admin/AdminDemoDatasets.vue'),
        meta: { title: '公共示例数据 - 管理后台 - BioIntelOS' }
      },
      {
        path: 'billing',
        component: () => import('../views/admin/AdminBillingManagement.vue'),
        children: [
          {
            path: '',
            name: 'AdminBilling',
            component: () => import('../views/admin/AdminBilling.vue'),
            meta: { title: '计费管理 - 管理后台 - BioIntelOS' }
          },
          {
            path: 'daily-token-usage',
            name: 'AdminDailyTokenUsage',
            component: () => import('../views/admin/AdminDailyTokenUsage.vue'),
            meta: { title: '每日 Token 用量 - 计费管理 - BioIntelOS' }
          },
          {
            path: 'model-token-usage',
            name: 'AdminModelTokenUsage',
            component: () => import('../views/admin/AdminModelTokenUsage.vue'),
            meta: { title: '按模型用量 - 计费管理 - BioIntelOS' }
          },
          {
            path: 'recharge-packages',
            name: 'AdminRechargePackages',
            component: () => import('../views/admin/AdminRechargePackages.vue'),
            meta: { title: '加油包档位 - 计费管理 - BioIntelOS' }
          },
          {
            path: 'subscription-plans',
            name: 'AdminSubscriptionPlans',
            component: () => import('../views/admin/AdminSubscriptionManagement.vue'),
            meta: { title: '订阅套餐管理 - BioIntelOS' }
          },
          {
            path: 'member-gifts',
            name: 'AdminMemberGifts',
            component: () => import('../views/admin/AdminSubscriptionManagement.vue'),
            meta: { title: '会员赠送管理 - BioIntelOS' }
          },
          {
            path: 'subscription-holders',
            name: 'AdminSubscriptionHolders',
            component: () => import('../views/admin/AdminSubscriptionHolders.vue'),
            meta: { title: '订阅持有 - BioIntelOS' }
          },
          {
            path: 'credit-logs',
            name: 'AdminCreditLogs',
            component: () => import('../views/admin/AdminCreditLogs.vue'),
            meta: { title: '碱基币流水 - 计费管理 - BioIntelOS' }
          },
          {
            path: 'recharge-orders',
            name: 'AdminRechargeOrders',
            component: () => import('../views/admin/AdminRechargeOrders.vue'),
            meta: { title: '充值审核 - 计费管理 - BioIntelOS' }
          },
          {
            path: 'invoices',
            name: 'AdminInvoices',
            component: () => import('../views/admin/AdminInvoices.vue'),
            meta: { title: '发票管理 - 计费管理 - BioIntelOS' }
          },
        ]
      },
      {path:'community-config',name:'AdminCommunityConfig',component:()=>import('../views/admin/AdminCommunityConfig.vue'),meta:{researchSharing:true,title:'成果社区配置 - BioIntelOS'}},
      {
        path: 'inspiration-cases',
        name: 'AdminInspirationCases',
        component: () => import('../views/admin/AdminInspirationCases.vue'),
        meta: { title: '个性化分析案例 - 管理后台 - BioIntelOS' }
      },
      {
        path: 'linux-users',
        name: 'AdminLinuxUsers',
        component: () => import('../views/admin/AdminLinuxUsers.vue'),
        meta: { title: 'Linux用户管理 - 管理后台 - BioIntelOS' }
      },
      {
        path: 'enterprises',
        name: 'AdminEnterprises',
        component: () => import('../views/admin/AdminEnterprises.vue'),
      },
      {
        path: 'whitelist',
        name: 'AdminWhitelist',
        component: () => import('../views/admin/AdminWhitelist.vue'),
        meta: { title: '白名单管理 - 管理后台 - BioIntelOS' }
      },
      // ── Agent Trace Monitor v1（plan §13） ──
      {
        path: 'scientific-reviews',
        name: 'AdminScientificReviews',
        component: () => import('../views/admin/AdminScientificReviews.vue'),
        meta: { title: '科学审查过程 - 管理后台 - BioIntelOS' }
      },
      {
        path: 'agent-trace',
        name: 'AdminAgentTrace',
        component: () => import('../views/admin/AdminAgentTrace.vue'),
        meta: { title: '智能体监控 - 管理后台 - BioIntelOS' }
      },
      {
        path: 'agent-trace/user/:userId',
        name: 'AdminAgentTraceUser',
        component: () => import('../views/admin/AdminAgentTraceUser.vue'),
        meta: { title: '用户追踪 - 管理后台 - BioIntelOS' }
      },
      {
        path: 'agent-trace/session/:sessionId',
        name: 'AdminAgentTraceSession',
        component: () => import('../views/admin/AdminAgentTraceSession.vue'),
        meta: { title: '会话链路 - 管理后台 - BioIntelOS' }
      },
      {
        path: 'analysis-packages',
        name: 'AdminAnalysisPackages',
        component: () => import('../views/admin/AdminAnalysisPackages.vue'),
        meta: { title: '分析包部署 - 管理后台 - BioIntelOS' }
      },
      {
        path: 'system-config',
        component: () => import('../views/admin/AdminSystemConfig.vue'),
        children: [
          {
            path: '',
            name: 'AdminFeatureToggles',
            component: () => import('../views/admin/system/AdminFeatureToggles.vue'),
            meta: { title: '功能开关 - 系统配置与管理 - BioIntelOS' }
          },
          {
            path: 'releases',
            name: 'AdminReleaseAnnouncements',
            component: () => import('../views/admin/AdminReleaseAnnouncements.vue'),
            meta: { title: '版本发布 - 系统配置与管理 - BioIntelOS' }
          },
          {
            path: 'system-parameters',
            name: 'AdminSystemParameters',
            component: () => import('../views/admin/system/AdminSystemParameters.vue'),
            meta: { title: '系统参数 - 系统配置与管理 - BioIntelOS' }
          },
          {
            path: 'promotion',
            name: 'AdminPromotion',
            component: () => import('../views/admin/AdminPromotion.vue'),
            meta: { title: '推广管理 - 系统配置与管理 - BioIntelOS' }
          },
          {
            path: 'maintenance-notice',
            name: 'AdminMaintenanceNotice',
            component: () => import('../views/admin/system/AdminMaintenanceNotice.vue'),
            meta: { title: '维护公告 - 系统配置与管理 - BioIntelOS' }
          },
          {
            path: 'redis-cache',
            name: 'AdminRedisCache',
            component: () => import('../views/admin/AdminRedisCache.vue'),
            meta: { title: 'Redis 缓存管理 - 系统配置与管理 - BioIntelOS' }
          },
          {
            path: 'rate-limit',
            name: 'AdminRateLimit',
            component: () => import('../views/admin/AdminRateLimit.vue'),
            meta: { title: '限流规则 - 系统配置与管理 - BioIntelOS' }
          },
          {
            path: 'invite-codes',
            name: 'AdminInviteCodes',
            component: () => import('../views/admin/AdminInviteCodes.vue'),
            meta: { title: '邀请码管理 - 系统配置与管理 - BioIntelOS' }
          },
          { path: 'messages', name: 'AdminMessageDiagnostics', component: () => import('../views/admin/AdminMessageDiagnostics.vue'), meta: { title: '消息排查' } },
          {
            path: 'send-logs',
            name: 'AdminSendLogs',
            component: () => import('../views/admin/AdminSendLogs.vue'),
            meta: { title: '发送日志 - 系统配置与管理 - BioIntelOS' }
          },
          {
            path: 'user-behavior',
            name: 'AdminUserBehavior',
            component: () => import('../views/admin/AdminUserBehavior.vue'),
            meta: { title: '用户行为分析 - 系统配置与管理 - BioIntelOS' }
          },
          {
            path: 'feedback',
            name: 'AdminFeedback',
            component: () => import('../views/admin/AdminFeedback.vue'),
            meta: { title: '问题反馈 - 系统配置与管理 - BioIntelOS' }
          },
        ]
      },
      {
        path: 'prompts',
        name: 'AdminPromptManagement',
        component: () => import('../views/admin/AdminPromptManagement.vue'),
        meta: { title: '提示词管理 - 管理后台 - BioIntelOS' }
      },
      {
        path: 'dataset-updates',
        name: 'AdminDatasetUpdates',
        component: () => import('../views/admin/AdminDatasetUpdates.vue'),
        meta: { title: '数据更新公告 - 管理后台 - BioIntelOS' }
      },
      // 旧地址只做兼容跳转，页面仅在新的 Tab 子路由中挂载。
      { path: 'promotion', redirect: '/admin/system-config/promotion' },
      { path: 'redis-cache', redirect: '/admin/system-config/redis-cache' },
      { path: 'rate-limit', redirect: '/admin/system-config/rate-limit' },
      { path: 'invite-codes', redirect: '/admin/system-config/invite-codes' },
      { path: 'send-logs', redirect: '/admin/system-config/send-logs' },
      { path: 'user-behavior', redirect: '/admin/system-config/user-behavior' },
      { path: 'recharge-packages', redirect: '/admin/billing/recharge-packages' },
      { path: 'credit-logs', redirect: '/admin/billing/credit-logs' },
      { path: 'recharge-orders', redirect: '/admin/billing/recharge-orders' },
      { path: 'invoices', redirect: '/admin/billing/invoices' },
    ],
  }
]

if (process.env.NODE_ENV === 'development') {
  routes.push({
    path: '/credit-center-preview',
    name: 'CreditCenterPreview',
    component: () => import('../views/CreditCenter.vue'),
    meta: { requiresAuth: false, title: '碱基币中心 UI 预览 - BioIntelOS' }
  })
}

// 创建路由实例
const giftPathMode = isShortGiftPath(window.location.pathname)
const router = createRouter({
  history: createEntryHistory(window.location.pathname),
  routes  // 注入路由规则
})

// 路由守卫 - 检查登录状态
router.beforeEach(async (to, from, next) => {
  const navigationTarget = giftHistoryNavigationTarget(giftPathMode, to)
  if (navigationTarget) {
    window.location.assign(navigationTarget)
    next(false)
    return
  }
  if (to.matched.some(record => record.meta.researchSharing) && !await loadResearchSharing()) {
    next({name:'AiChat'}); return
  }
  const token = localStorage.getItem('token')
  const isLoggedIn = !!token

  // 动态设置页面标题
  if (to.meta.title) {
    document.title = to.meta.title
  } else {
    document.title = 'BioIntelOS - AI for Science Platform'
  }

  if (to.meta.requiresAuth && !isLoggedIn) {
    // 需要登录但未登录，跳转到登录页
    next({ name: 'Login', query: { redirect: to.fullPath } })
  } else if (to.name === 'Login' && isLoggedIn) {
    // 已登录用户访问登录页，跳转到AI聊天页
    next({ name: 'AiChat' })
  } else if (to.name === 'Home' && isLoggedIn) {
    // 已登录用户访问首页，跳转到AI聊天页
    next({ name: 'AiChat' })
  } else if (to.matched.some(record => record.meta.requiresAdmin)) {
    // 需要管理员权限，检查角色
    let userObj = null
    try { userObj = JSON.parse(localStorage.getItem('user') || '{}') } catch { userObj = null }
    if (!userObj || userObj.role !== 'admin') {
      next({ name: 'AiChat' })
    } else {
      next()
    }
  } else if (to.matched.some(record => record.meta.requiresEnterpriseAdmin)) {
    // 需要企业管理员权限
    let userObj = null
    try { userObj = JSON.parse(localStorage.getItem('user') || '{}') } catch { userObj = null }
    if (!userObj || userObj.role !== 'enterprise_admin') {
      next({ name: 'AiChat' })
    } else {
      next()
    }
  } else if (to.matched.some(record => record.meta.requiresPersonalUser)) {
    // 仅个人用户可访问（企业身份不可见推广功能）
    let userObj = null
    try { userObj = JSON.parse(localStorage.getItem('user') || '{}') } catch { userObj = null }
    const role = userObj?.role
    if (!userObj || (role !== 'user' && role !== 'admin')) {
      next({ name: 'AiChat' })
    } else {
      next()
    }
  } else {
    next()
  }
})

// 发布新版前端后，旧页面加载已删除的分包会失败：整页刷新到目标页
router.onError((error, to) => { reloadForStaleChunk(error, to) })

export default router  // 导出路由，供 main.js 引入
